import { useEffect, useState } from 'react';

// Cache holds the last-known flags map between mounts. We DO refresh on
// window focus so admin toggles propagate to other open tabs without a
// full reload, and we DO surface a refetch helper for explicit refresh.
let cachedFlags = null;
let cachePromise = null;
const subscribers = new Set();

function fetchFlags() {
  cachePromise = fetch('/api/feature-flags/public')
    .then(r => r.ok ? r.json() : {})
    .catch(() => ({}));
  return cachePromise.then((m) => {
    cachedFlags = m;
    for (const fn of subscribers) fn(m);
    return m;
  });
}

if (typeof window !== 'undefined') {
  // Refresh when the user returns to the tab — flags toggled in another tab
  // (or in admin) will then propagate within a few hundred ms instead of
  // waiting for a hard reload. Throttled to once per 30s.
  let lastFocus = 0;
  window.addEventListener('focus', () => {
    if (Date.now() - lastFocus < 30_000) return;
    lastFocus = Date.now();
    fetchFlags();
  });
}

// Fetches enabled feature flags. Usage:
//   const flags = useFeatureFlags();  if (flags.auctions) { ... }
//   useFeatureFlags.refresh()  // force a refetch (e.g. after admin save)
export default function useFeatureFlags() {
  const [flags, setFlags] = useState(cachedFlags || {});
  useEffect(() => {
    subscribers.add(setFlags);
    if (cachedFlags) setFlags(cachedFlags);
    else if (!cachePromise) fetchFlags();
    else cachePromise.then(setFlags);
    return () => { subscribers.delete(setFlags); };
  }, []);
  return flags;
}

useFeatureFlags.refresh = fetchFlags;
